import { useRef, useState, useEffect } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { doc, getDoc } from "firebase/firestore";
import { getAuth, signOut } from "firebase/auth";
import { alpha } from "@mui/material/styles";
import {
  Button,
  Box,
  Divider,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from "@mui/material";
import Iconify from "../../components/Iconify";
import MenuPopover from "../../components/MenuPopover";
import account from "../../_mocks_/account";

import { useUser } from "../../UserContext";
import { db } from "../../firebase";


const MENU_OPTIONS = [
  {
    label: "Home",
    icon: "eva:home-fill",
    linkTo: "/",
  },
  {
    label: "Profile",
    icon: "eva:person-fill",
    linkTo: "/dashboard/profile",
  },
];

export default function AccountPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const [switchDialogOpen, setSwitchDialogOpen] = useState(false);
  const [childProfiles, setChildProfiles] = useState([]);
  const [parentProfile, setParentProfile] = useState(null);

  const { user, memberData, switchMember } = useUser();

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleLogout = async () => {
    try {
      const auth = getAuth();
      await signOut(auth);
      localStorage.clear();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleOpenSwitchDialog = async () => {
    setOpen(false);
    setSwitchDialogOpen(true);

    if (memberData?.childrenIds?.length) {
      try {
        const arr = await Promise.all(
          memberData.childrenIds.map(async (childId) => {
            const snap = await getDoc(doc(db, "members", childId));
            if (snap.exists()) {
              return { id: snap.id, ...snap.data() };
            }
            return null;
          })
        );
        const valid = arr.filter(Boolean);
        setChildProfiles(valid);
      } catch (err) {
        console.error("Error fetching child docs:", err);
        setChildProfiles([]);
      }
    } else {
      setChildProfiles([]);
    }

    if (memberData?.parentId) {
      try {
        const parentSnap = await getDoc(doc(db, "members", memberData.parentId));
        if (parentSnap.exists()) {
          setParentProfile({ id: parentSnap.id, ...parentSnap.data() });
        } else {
          setParentProfile(null);
        }
      } catch (err) {
        console.error("Error fetching parent doc:", err);
        setParentProfile(null);
      }
    } else {
      setParentProfile(null);
    }
  };

  const handleCloseSwitchDialog = () => {
    setSwitchDialogOpen(false);
  };

  const handleSwitchProfile = async (memberId) => {
    await switchMember(memberId);
    setSwitchDialogOpen(false);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          padding: 0,
          width: 44,
          height: 44,
          ...(open && {
            "&:before": {
              zIndex: 1,
              content: "''",
              width: "100%",
              height: "100%",
              borderRadius: "50%",
              position: "absolute",
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.72),
            },
          }),
        }}
      >
        <Avatar src={memberData?.profilePic || account.photoURL} alt="photoURL" />
      </IconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 220 }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle1" noWrap>
            {memberData
              ? `${memberData.firstName || ""} ${memberData.lastName || ""}`
              : account.displayName}
          </Typography>
          <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
            {memberData?.email || account.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 1 }} />

        {MENU_OPTIONS.map((option) => (
          <MenuItem
            key={option.label}
            to={option.linkTo}
            component={RouterLink}
            onClick={handleClose}
            sx={{ typography: "body2", py: 1, px: 2.5 }}
          >
            <Iconify
              icon={option.icon}
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            {option.label}
          </MenuItem>
        ))}

        {(memberData?.childrenIds?.length > 0 || memberData?.parentId) && (
          <MenuItem
            onClick={handleOpenSwitchDialog}
            sx={{ typography: "body2", py: 1, px: 2.5 }}
          >
            <Iconify
              icon="ic:round-switch-account"
              sx={{
                mr: 2,
                width: 24,
                height: 24,
              }}
            />
            Switch Profile
          </MenuItem>
        )}

        <Box sx={{ p: 2, pt: 1.5 }}>
          <Button fullWidth color="inherit" variant="outlined" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </MenuPopover>

      <Dialog
        open={switchDialogOpen}
        onClose={handleCloseSwitchDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Switch Profile</DialogTitle>
        <DialogContent dividers>
          {parentProfile && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Switch to Parent:
              </Typography>
              <ListItem
                button
                onClick={() => handleSwitchProfile(parentProfile.id)}
              >
                <ListItemText
                  primary={`${parentProfile.firstName || ""} ${parentProfile.lastName || ""}`}
                  secondary={parentProfile.email}
                />
              </ListItem>
              <Divider sx={{ my: 2 }} />
            </>
          )}

          {childProfiles.length > 0 && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Switch to Child:
              </Typography>
              <List dense>
                {childProfiles.map((child) => (
                  <ListItem
                    button
                    key={child.id}
                    onClick={() => handleSwitchProfile(child.id)}
                  >
                    <ListItemText
                      primary={`${child.firstName || ""} ${child.lastName || ""}`}
                      secondary={child.email}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {!parentProfile && childProfiles.length === 0 && (
            <Typography variant="body2">
              No related family profiles found.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSwitchDialog}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
